import zip from 'lodash/zip';
import size from 'lodash/size';
import unzip from 'lodash/unzip';

import { createSelector } from 'reselect';

import {
  mean,
  medianSorted,
  linearRegression,
  sampleStandardDeviation as stdev,
  sampleCorrelation as correlation
} from 'simple-statistics';

import getParent from '../selectors';

import {
  getPrimaryParameter, getPrimaryParameterType, getPrimaryParameterValues
} from 'store/scenes/randomizationHypothesisTesting/primaryParameter/selectors';

import { getTestSelection } from '../testSelection';
import { getSelectedSampleId } from '../selectedSampleId';
import { getSecondaryParameter } from '../secondaryParameter';
import { getPrimaryParameterLevel } from '../primaryParameterLevel';

const getSamples = state => getParent(state).samples;

export const getSamplesAmount = createSelector(getSamples, size);

export const getSelectedSample = createSelector(
  getSamples,
  getSelectedSampleId,

  (samples, selectedSampleId) => samples[selectedSampleId]
);

export const getSelectedSampleSummary = createSelector(
  getSelectedSample, s => ({
    mean: mean(s),
    stdev: s.length >= 2 ? stdev(s) : 'N/A',
    amount: s.length
  })
);

export const getOriginalSampleSummary = createSelector(
  getPrimaryParameterValues, v => ({
    mean: mean(v),
    stdev: v.length >= 2 ? stdev(v) : 'N/A',
    amount: v.length
  })
);

export const doSamplesExist = state =>
  getSamples(state).length !== 0;

const makeNumericalSelector = fn =>
  createSelector(getSamples,
    samples => samples.map(sample => (sample.length >= 2 && fn !== stdev) ? fn(sample) : null));

export const getMeans = makeNumericalSelector(mean);
export const getStdevs = makeNumericalSelector(stdev);
export const getMedians = makeNumericalSelector(medianSorted);

export const getSlopes = makeNumericalSelector(
  sample => linearRegression(sample).m
);

export const getCorrelations = makeNumericalSelector(
  sample => sample.length >= 2 ? correlation(...unzip(sample)) : null
);

export const getProportions = createSelector(
  getSamples,
  getPrimaryParameterLevel,

  (samples, level) => samples.map(s =>
    s.filter(v => String(v) === level).length / s.length
  )
);

export const getCounts = createSelector(
  getSamples,
  getPrimaryParameterLevel,

  (samples, level) => samples.map(s =>
    s.filter(v => String(v) === level).length
  )
);

export const getSamplesSpecificationsTableData = state => {
  const primaryParameter = getPrimaryParameter(state);
  const primaryParameterLevel = getPrimaryParameterLevel(state);

  const bootstrappedSample = getSelectedSample(state);

  const predicate = v => v === primaryParameterLevel;

  return {
    totalAmount: {
      original: primaryParameter.values.length,
      bootstrapped: bootstrappedSample.length
    },
    levelAmount: {
      original: primaryParameter.values.filter(predicate).length,
      bootstrapped: bootstrappedSample.filter(predicate).length
    }
  };
};

export const getValues = state => {
  const secondaryParameter = getSecondaryParameter(state);
  const parameterType = getPrimaryParameterType(state);
  const testSelection = getTestSelection(state);

  if (parameterType === 'numerical') {
    if (secondaryParameter.name) {
      switch (testSelection) {
        case 'slope':
          return getSlopes(state);
        case 'correlation':
          return getCorrelations(state);
        default:
          return [];
      }
    }

    switch (testSelection) {
      case 'mean':
        return getMeans(state);
      case 'median':
        return getMedians(state);
      case 'stdev':
        return getStdevs(state);
      default:
        return [];
    }
  }

  if (parameterType === 'categorical') {
    switch (testSelection) {
      case 'proportion':
        return getProportions(state);
      case 'count':
        return getCounts(state);
      default:
        return [];
    }
  }

  return [];
};

export const getSamplesMean = createSelector(getValues, mean);
export const getSamplesStdev = createSelector(getValues, stdev);

export const getSimulatedPLineX = createSelector(
  getTestSelection,
  getPrimaryParameter,
  getPrimaryParameterLevel,
  getSecondaryParameter,

  (testSelection, primaryParameter, primaryParameterLevel, secondaryParameter) => {
    switch (testSelection) {
      case 'mean':
        return mean(primaryParameter.values);

      case 'count':
        return primaryParameter.values.filter(
          v => v === primaryParameterLevel
        ).length;

      case 'proportion': {
        const primaryParameterLevelCount =
          primaryParameter.values.filter(
            v => v === primaryParameterLevel
          ).length;

        const primaryParameterTotalCount =
          primaryParameter.values.length;

        return primaryParameterLevelCount / primaryParameterTotalCount;
      }

      case 'slope':
        return linearRegression(
          zip(
            primaryParameter.values,
            secondaryParameter.values
          )
        ).m;

      case 'correlation':
        return primaryParameter.values.length > 1 && secondaryParameter.values.length > 1 ? correlation(
          primaryParameter.values,
          secondaryParameter.values
        ) : 'N/A';

      default:
        return null;
    }
  }
);

export default getSamples;
